import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtQty } from './InventoryShared';

const MOVEMENT_TYPES = ['opening_stock', 'receiving', 'delivery', 'transfer_out', 'transfer_in', 'adjustment_increase', 'adjustment_decrease', 'return_restock', 'return_writeoff', 'return_damaged', 'return_supplier'];

export default function InventoryMovements() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [movementType, setMovementType] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/movements?per_page=50`;
      if (movementType) url += `&movement_type=${movementType}`;
      if (warehouseId) url += `&warehouse_id=${warehouseId}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, movementType, warehouseId]);

  const fetchAux = useCallback(async () => {
    const [pRes, wRes] = await Promise.all([
      fetch(`${API}/companies/${companyId}/inventory/products?per_page=200`, { headers: getAuthHeaders() }),
      fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() }),
    ]);
    const pJson = await pRes.json();
    setProducts(Array.isArray(pJson.data) ? pJson.data : []);
    setWarehouses(await wRes.json());
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const reverse = async (id) => {
    if (!window.confirm('Reverse this movement?')) return;
    const res = await fetch(`${API}/companies/${companyId}/inventory/movements/${id}/reverse`, { method: 'POST', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🔄 Stock Movements</h2>
        <button style={btnPrimary} onClick={() => setCreating(true)}>+ Record Movement</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 200 }} value={movementType} onChange={e => setMovementType(e.target.value)}>
          <option value="">All Types</option>
          {MOVEMENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={{ ...inputStyle, width: 200 }} value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
          <option value="">All Warehouses</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Movement #', 'Product', 'Warehouse', 'Type', 'Direction', 'Qty', 'Status', 'Date', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No movements</td></tr>}
              {list.map(m => (
                <tr key={m.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{m.movement_number}</td>
                  <td style={tdStyle}>{m.product?.name}</td>
                  <td style={tdStyle}>{m.warehouse?.name}</td>
                  <td style={tdStyle}>{(m.movement_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{m.direction}</td>
                  <td style={tdStyle}>{fmtQty(m.quantity)}</td>
                  <td style={tdStyle}><Badge value={m.status} /></td>
                  <td style={tdStyle}>{m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>
                    {m.status === 'posted' && <button style={btnSmall} onClick={() => reverse(m.id)}>Reverse</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <MovementModal companyId={companyId} products={products} warehouses={warehouses}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchList(); }} />
      )}
    </div>
  );
}

function MovementModal({ companyId, products, warehouses, onClose, onSaved }) {
  const [form, setForm] = useState({
    product_id: '', warehouse_id: '', movement_type: 'receiving', direction: 'in',
    quantity: '', unit_cost: '', to_location_id: '', from_location_id: '', notes: '',
  });
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.warehouse_id) { setLocations([]); return; }
    fetch(`${API}/companies/${companyId}/inventory/warehouses/${form.warehouse_id}/locations`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(setLocations).catch(() => setLocations([]));
  }, [form.warehouse_id, companyId]);

  const setType = (t) => {
    const direction = ['receiving', 'opening_stock', 'transfer_in', 'adjustment_increase', 'return_restock', 'return_damaged', 'return_writeoff', 'return_supplier'].includes(t) ? 'in' : 'out';
    setForm({ ...form, movement_type: t, direction });
  };

  const save = async () => {
    if (!form.product_id || !form.warehouse_id || !form.quantity) { alert('Product, warehouse, and quantity are required'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/movements`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Record Stock Movement</h3>
        <FormRow label="Movement Type *">
          <select style={inputStyle} value={form.movement_type} onChange={e => setType(e.target.value)}>
            {MOVEMENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </FormRow>
        <FormRow label="Product *">
          <select style={inputStyle} value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })}>
            <option value="">— Select —</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.product_code})</option>)}
          </select>
        </FormRow>
        <FormRow label="Warehouse *">
          <select style={inputStyle} value={form.warehouse_id} onChange={e => setForm({ ...form, warehouse_id: e.target.value })}>
            <option value="">— Select —</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </FormRow>
        <div style={{ display: 'flex', gap: 10 }}>
          {form.direction === 'in' && (
            <div style={{ flex: 1 }}><FormRow label="To Location">
              <select style={inputStyle} value={form.to_location_id} onChange={e => setForm({ ...form, to_location_id: e.target.value })}>
                <option value="">— None —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
              </select>
            </FormRow></div>
          )}
          {form.direction === 'out' && (
            <div style={{ flex: 1 }}><FormRow label="From Location">
              <select style={inputStyle} value={form.from_location_id} onChange={e => setForm({ ...form, from_location_id: e.target.value })}>
                <option value="">— None —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
              </select>
            </FormRow></div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Quantity *"><input type="number" style={inputStyle} value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Unit Cost"><input type="number" style={inputStyle} value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: e.target.value })} /></FormRow></div>
        </div>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Post Movement'}</button>
        </div>
      </div>
    </div>
  );
}
